<template>
  <v-container>
    <v-container>
      <v-card tile>
        <v-card-title>
          {{ $t("paymentsTitle") }}
          <v-btn icon @click.stop="paymentForm = true"
            ><v-icon>mdi-plus-circle</v-icon></v-btn
          >
          <payments-form
            :paymentForm="paymentForm"
            @reload-payments="uploadPayments"
            @close-dialog="paymentForm = false"
          ></payments-form>
        </v-card-title>
        <v-container class="py-5 px-3">
          <v-row>
            <v-col cols="12" sm="2" class="py-0">
              <v-menu
                v-model="datepickerStart"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="
                      moment(selectedFilters.date_start).format('DD.MM.YYYY')
                    "
                    :label="$t('paymentsFilterStart')"
                    type="text"
                    dense
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    prepend-icon="mdi-calendar-start"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :max="moment.parseZone().format('YYYY-MM-DD')"
                  v-model="selectedFilters.date_start"
                  no-title
                  scrollable
                  :locale="$i18n.locale"
                  @change="uploadPayments()"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="2" class="py-0">
              <v-menu
                v-model="datepickerEnd"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="
                      moment(selectedFilters.date_end).format('DD.MM.YYYY')
                    "
                    :label="$t('paymentsFilterEnd')"
                    type="text"
                    dense
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    prepend-icon="mdi-calendar-end"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :min="
                    moment
                      .parseZone(selectedFilters.date_start)
                      .format('YYYY-MM-DD')
                  "
                  :max="moment.parseZone().format('YYYY-MM-DD')"
                  v-model="selectedFilters.date_end"
                  no-title
                  scrollable
                  :locale="$i18n.locale"
                  @change="uploadPayments()"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="4" class="py-0">
              <v-select
                item-text="title"
                item-value="id"
                prepend-icon="mdi-format-list-bulleted-type"
                dense
                :label="$t('paymentsFilterTypes')"
                :items="payment_types"
                v-model="computedTypes"
                @change="uploadPayments()"
                multiple
              >
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0">{{ item.title }}</span>
                  <span v-if="index === 1" class="grey--text caption ml-1"
                    >(+{{ selectedFilters.payment_type.length - 1 }})</span
                  >
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="2" class="py-0">
              <v-select
                item-text="title"
                item-value="value"
                prepend-icon="mdi-cash-register"
                dense
                :label="$t('paymentsFilterPurchase')"
                :items="purchase_signs"
                v-model="selectedFilters.purchase_sign"
                @change="uploadPayments()"
              ></v-select>
            </v-col>
            <v-col class="py-0 col-auto ml-auto">
              <v-btn
                @click="uploadPayments((csv = true))"
                outlined
                color="green darken-1"
                text
                tile
                :loading="loadingCSV"
                :disabled="payments == null || payments == [] || loadingCSV"
                ><v-icon left>mdi-file-export</v-icon>CSV</v-btn
              >
            </v-col>
          </v-row>
        </v-container>
        <v-container>
          <v-data-table
            v-if="payments"
            :headers="headers"
            :items="payments"
            :items-per-page="20"
            class="elevation-1"
            :no-data-text="$t('paymentsNoData')"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, -1],
              'items-per-page-text': $t('paymentsRowsPerPage'),
            }"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
          >
            <template v-slot:item.order="{ item }">
              <router-link
                :to="{ name: 'order', params: { order_id: item.order } }"
                target="_blank"
                style="text-decoration: none"
                >{{ item.order }}</router-link
              >
            </template>
            <template v-slot:item.payment_type="{ item }">
              <span>{{ translateFilter(item.payment_type) }}</span>
            </template>
            <template v-slot:item.created="{ item }">
              <span>{{ item.created | dateFilter }}</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon
                v-if="item.payment_type == 7"
                @click="openHideDialog(item)"
                >mdi-eye-off</v-icon
              >
            </template>
            <template v-slot:top>
              <v-dialog v-model="dialogHide" max-width="600px">
                <v-card tile>
                  <v-card-title class="headline"
                    >{{ $t("paymentsHideQuestion") }}
                    {{ selectedPayment ? selectedPayment.id : "-" }}
                    ?</v-card-title
                  >
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red darken-1" text @click="closeHideDialog">{{
                      $t("btnNo")
                    }}</v-btn>
                    <v-btn
                      color="green darken-1"
                      text
                      @click="confirmHideDialog"
                      >{{ $t("btnYes") }}</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </template>
          </v-data-table>
          <v-skeleton-loader
            v-else
            class="mx-n3 mt-n4"
            type="table-thead, table-tbody, table-tfoot"
            tile
          ></v-skeleton-loader>
        </v-container>
      </v-card>
    </v-container>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import PaymentsForm from "@/components/PaymentsForm.vue";
import moment from "moment";
import store from "@/store";

export default {
  name: "Payments",

  beforeRouteEnter: async (to, from, next) => {
    await store.dispatch("loadLocation", to.params.location_id);
    next();
  },

  data() {
    return {
      loadingCSV: false,
      datepickerStart: false,
      datepickerEnd: false,
      paymentForm: false,
      dialogHide: false,
      payments: null,
      paymentsCSV: null,
      selectedPayment: null,
      sortBy: "created",
      sortDesc: true,
      headers: [
        { text: "ID", value: "id", width: "5%" },
        { text: this.$i18n.t("ordersID"), value: "order", width: "7%" },
        {
          text: this.$i18n.t("paymentsType"),
          value: "payment_type",
          width: "10%",
        },
        {
          text: this.$i18n.t("paymentsCreated"),
          value: "created",
          width: "7%",
        },
        { text: this.$i18n.t("paymentsStatus"), value: "status", width: "10%" },
        {
          text: this.$i18n.t("paymentsExternal"),
          value: "external_id",
          width: "12%",
        },
        {
          text: this.$i18n.t("paymentsPurchase"),
          value: "purchase",
          width: "8%",
        },
        {
          text: this.$i18n.t("paymentsComment"),
          value: "comment",
          width: "15%",
        },
        { text: this.$i18n.t("paymentsUser"), value: "user", width: "15%" },
        {
          text: this.$i18n.t("paymentsManage"),
          value: "actions",
          align: "end",
          sortable: false,
          width: "8%",
        },
      ],
      payment_types: [
        {
          id: "1",
          title: this.$i18n.t("paymentsType1"),
        },
        {
          id: "2",
          title: this.$i18n.t("paymentsType2"),
        },
        {
          id: "3",
          title: this.$i18n.t("paymentsType3"),
        },
        {
          id: "4",
          title: this.$i18n.t("paymentsType4"),
        },
        {
          id: "5",
          title: this.$i18n.t("paymentsType5"),
        },
        {
          id: "6",
          title: this.$i18n.t("paymentsType6"),
        },
        {
          id: "7",
          title: this.$i18n.t("paymentsType7"),
        },
      ],
      purchase_signs: [
        {
          value: "0",
          title: this.$i18n.t("paymentsSignsAll"),
        },
        {
          value: "1",
          title: this.$i18n.t("paymentsSignsP"),
        },
        {
          value: "-1",
          title: this.$i18n.t("paymentsSignsN"),
        },
      ],
      selectedFilters: {
        date_start: moment.parseZone().startOf("month").format("YYYY-MM-DD"),
        date_end: moment.parseZone().format("YYYY-MM-DD"),
        payment_type: [],
        purchase_sign: 0,
      },
    };
  },
  mounted() {
    this.uploadPayments();
  },
  computed: {
    ...mapGetters(["location"]),
    computedTypes: {
      get: function () {
        return Array.from(this.selectedFilters.payment_type);
      },
      set: function (value) {
        this.selectedFilters.payment_type = value;
      },
    },
  },
  filters: {
    dateFilter(value) {
      return moment(value).format("DD.MM.YYYY");
    },
  },
  components: {
    PaymentsForm,
  },
  methods: {
    ...mapActions(["getPayments", "hidePayment", "getPaymentsCSV"]),
    translateFilter(filter) {
      switch (filter) {
        case 1:
          return this.$i18n.t("paymentsType1");
        case 2:
          return this.$i18n.t("paymentsType2");
        case 3:
          return this.$i18n.t("paymentsType3");
        case 4:
          return this.$i18n.t("paymentsType4");
        case 5:
          return this.$i18n.t("paymentsType5");
        case 6:
          return this.$i18n.t("paymentsType6");
        case 7:
          return this.$i18n.t("paymentsType7");
      }
    },
    openHideDialog(item) {
      this.dialogHide = true;
      this.selectedPayment = item;
    },
    closeHideDialog() {
      this.dialogHide = false;
    },
    confirmHideDialog() {
      this.hidePayment(this.selectedPayment.id).then((response) => {
        this.payments = null;
        this.uploadPayments();
      });
      this.dialogHide = false;
    },
    loadPayments(payload) {
      this.payments = null;
      this.getPayments(payload).then((response) => {
        if (response.data) {
          this.payments = response.data;
        } else {
          this.payments = [];
        }
      });
    },
    uploadPayments(csv) {
      const filteredFilters = Object.entries(this.selectedFilters).reduce(
        (a, [k, v]) =>
          typeof v === "object" && !v.length ? a : ((a[k] = v), a),
        {}
      );
      const payload = Object.keys(filteredFilters)
        .map((key) => key + "=" + filteredFilters[key])
        .join("&");

      if (csv) {
        this.loadingCSV = true;
        this.getPaymentsCSV(payload).then((response) => {
          if (response.data) {
            this.paymentsCSV = response.data;
            this.loadingCSV = false;
            this.downloadCSV();
          } else {
            this.paymentsCSV = null;
          }
        });
      } else {
        this.loadPayments(payload);
      }
    },
    downloadCSV() {
      const anchor = document.createElement("a");
      anchor.href =
        "data:text/csv;charset=utf-8," + encodeURIComponent(this.paymentsCSV);
      anchor.target = "_blank";
      anchor.download = "payments.csv";
      anchor.click();
    },
  },
};
</script>

<style scoped>
.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}
@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
